/*
 * Changes:
 * - Added 'use client' directive for Next.js 13+ compatibility
 * - File contains client-side features: React hooks, context provider, and custom hooks
 */

'use client'

import {createContext, useContext} from 'react'
import {useSimpleQuery} from '@frnt/base/hooks'
import {ResourceApiServiceProvider} from './resource-api-service-provider'
import type {BankAccount, LoanDepositInfo, LoanWithIncludes, OrganizationAccount, User} from '@frnt/lending-api/models'
import type {QueryObserverSuccessResult} from 'react-query'
import qs from 'qs'

interface TransactionPayload {
  amount: string
  type: 'collateral' | 'principal'
  side?: 'borrower' | 'lender'
}

interface LendingService {
  axios: any
  createAccount: (company_name: string, payload: {company_name: string; is_borrower: boolean; is_lender: boolean}) => Promise<any>
  createLoan: (payload: any) => Promise<any>
  createWithdrawal: (id: string, payload: TransactionPayload) => Promise<any>
  createDeposit: (id: string, payload: TransactionPayload) => Promise<any>
  updateLoan: (id: string, payload: any) => Promise<any>
  deleteLoan: (id: string) => Promise<any>
  updateFiatDetails: (id: string, payload: any) => Promise<any>
  updateInterestSchedule: (id: string, interestScheduleId: number, payload: any) => Promise<any>
  listAccounts: () => Promise<OrganizationAccount[]>
  listPayments: () => Promise<any>
  getLoan: (id: string) => Promise<any>
  getDepositInfo: (id: string) => Promise<any>
  listLoans: (query?: any) => Promise<any>
  getAccount: (id: string) => Promise<OrganizationAccount>
  recordPayment: (payload: any) => Promise<any>
  listEvents: (loan_id: string, query?: {page?: number; pageSize?: number}) => Promise<any>
  liquidateLoan: (loan_id: string, collateral_currency_code: string, amount: string) => Promise<any>
  updateLiquidationSettlementPrice: (loan_id: string, liquidation_id: number, payload: {settlement_price: number; settlement_notes?: string}) => Promise<any>
  updateLoanSettlementPrice: (loan_id: string, payload: {settlement_price: number; settlement_notes?: string}) => Promise<any>
  getLiquidationEvents: (loan_id: string) => Promise<any>
  postAddress: (payload: {loanId: string; address: string; currency_code: string; side: 'borrower' | 'lender'}) => Promise<any>
  updateAccount: (id: string, payload: {is_borrower?: boolean; is_lender?: boolean; manager_user_id?: string; company_name?: string}) => Promise<OrganizationAccount>
  createBankAccount: (payload: BankAccount) => Promise<any>
  updateBankAccount: (id: string, payload: BankAccount) => Promise<any>
  assignBankAccountToLoan: (loanId: string, bankAccountId: string, side: 'borrower' | 'lender') => Promise<any>
  removeBankAccountFromLoan: (loanId: string, side: 'borrower' | 'lender') => Promise<any>
  removeBankAccount: (id: string) => Promise<any>
  getBankAccount: (id: string) => Promise<any>
  getBankAccountsByLoanId: (loanId: string) => Promise<any>
  getSupportedCurrencies: () => Promise<{collateral_currencies: {value: string; label: string}[]; principal_currencies: {value: string; label: string}[]}>
  updateOrganizationStatus: (id: string, role: 'borrower' | 'lender', enabled: boolean) => Promise<any>
  updateOrganizationManagerStatus: (id: string, enabled: boolean) => Promise<any>
  getOrganizationStatus: (id: string) => Promise<OrganizationAccount>
  removeUserFromOrganization: (organizationId: string, userId: string) => Promise<any>
  addUserToOrganization: (organizationId: string, payload: {email: string; first_name: string; last_name: string}) => Promise<any>
  updateCustodyAddress: (payload: {loanId: string; address: string; reason: string}) => Promise<any>
  updateOrganizationTimezone: (id: string, payload: {timezone: string}) => Promise<any>
  validateLoanActivation: (id: string) => Promise<any>
  getLoanPreferences: (loan_id: string) => Promise<any>
  updateLoanPreferences: (preferences_id: string, payload: any) => Promise<any>
  createLoanPreferences: (payload: any) => Promise<any>
}

const LendingServiceContext = createContext<LendingService | undefined>(undefined)

interface LendingServiceProviderProps {
  children: any
  baseApiHost: string
  handleUnauthorized?: () => void
}

type CreateLoanPayload = {
  amount: number
  lending_currency_code: string
  collateral_currency_code: string
  collateral_requirement_percentage: number
  collateral_liquidation_percentage: number
  interest_rate: number
  admin_fee: number
  start_date: string
  end_date: string
  borrower_id: string
  lender_id: string
  enterprise_id: string
  wallet_id: string
}

const LendingServiceProvider = ({
  children,
  baseApiHost,
  handleUnauthorized = () => {
    // Do nothing
  }
}: LendingServiceProviderProps) => {
  const API_URL = '/api/v1/lending'

  return (
    <ResourceApiServiceProvider
      Context={LendingServiceContext}
      handleUnauthorized={handleUnauthorized}
      host={baseApiHost}
      timeout={600000}
      valueCallback={(axios) => ({
        createAccount: async (
          company_name: string,
          payload: {
            company_name: string
            is_borrower: boolean
            is_lender: boolean
          }
        ) => {
          const {data} = await axios.post(`${API_URL}/accounts`, payload)
          return data
        },
        updateInterestSchedule: async (id: string, interestScheduleId: number, payload: any) => {
          const {data} = await axios.patch(`${API_URL}/loans/${id}/interest-schedule/${interestScheduleId}`, payload)
          return data
        },
        listAccounts: async () => {
          const {data} = await axios.get(`${API_URL}/accounts`)
          return data
        },
        listPayments: async () => {
          const {data} = await axios.get(`${API_URL}/payments`)
          return data
        },
        getAccount: async (id: string) => {
          const {data} = await axios.get(`${API_URL}/accounts/${id}`)
          return data
        },
        getDepositInfo: async (id: string) => {
          const {data} = await axios.get(`${API_URL}/loans/${id}/principal/depositInfo?accountType=lender`)
          return data
        },
        createLoan: async (payload: CreateLoanPayload) => {
          const {data} = await axios.post(`${API_URL}/loans`, payload)
          return data
        },
        updateLoan: async (id: string, payload: any) => {
          const {data} = await axios.patch(`${API_URL}/loans/${id}`, payload)
          return data
        },
        createWithdrawal: async (id: string, payload: TransactionPayload) => {
          const {data} = await axios.post(`${API_URL}/loans/${id}/withdrawals`, payload)
          return data
        },
        createDeposit: async (id: string, payload: TransactionPayload) => {
          const {data} = await axios.post(`${API_URL}/loans/${id}/deposits`, payload)
          return data
        },
        deleteLoan: async (id: string) => {
          const {data} = await axios.delete(`${API_URL}/loans/${id}`)
          return data
        },
        updateFiatDetails: async (id: string, payload: any) => {
          const {data} = await axios.post(`${API_URL}/loans/${id}/fiat`, payload)
          return data
        },
        getLoan: async (id: string) => {
          const {data} = await axios.get(`${API_URL}/loans/${id}`)
          return data
        },
        listLoans: async (query?: any) => {
          // Transform the sorted array into a simpler format
          if (query?.sorted?.length > 0) {
            const sortItem = query.sorted[0]
            const field = Object.keys(sortItem)[0]
            const direction = sortItem[field]
            query = {
              ...query,
              sorted: `${field},${direction}`
            }
          }
          const {data} = await axios.get(`${API_URL}/loans${query ? qs.stringify(query, {addQueryPrefix: true}) : ''}`)
          return data
        },
        recordPayment: async (payload: any) => {
          const {data} = await axios.post(`${API_URL}/payments`, payload)
          return true
        },
        listEvents: async (loan_id: string, query?: {page?: number; pageSize?: number}) => {
          const {data} = await axios.get(`${API_URL}/loans/${loan_id}/events${query ? qs.stringify(query, {addQueryPrefix: true}) : ''}`)
          return data
        },
        liquidateLoan: async (loan_id: string, collateral_currency_code: string, amount: string) => {
          const {data} = await axios.post(`${API_URL}/loans/${loan_id}/liquidations`, {collateral_currency_code, amount})
          return data
        },
        updateLiquidationSettlementPrice: async (loan_id: string, liquidation_id: number, payload: {settlement_price: number; settlement_notes?: string}) => {
          const {data} = await axios.patch(`${API_URL}/loans/${loan_id}/liquidations/${liquidation_id}/settlement-price`, payload)
          return data
        },
        updateLoanSettlementPrice: async (loan_id: string, payload: {settlement_price: number; settlement_notes?: string}) => {
          const {data} = await axios.patch(`${API_URL}/loans/${loan_id}/settlement-price`, payload)
          return data
        },
        getLiquidationEvents: async (loan_id: string) => {
          const {data} = await axios.get(`${API_URL}/loans/${loan_id}/liquidations`)
          return data
        },
        postAddress: async (payload: {loanId: string; address: string; currency_code: string; side: 'borrower' | 'lender'}) => {
          const {data} = await axios.post(`${API_URL}/whitelisted-addresses`, payload)
          return data
        },
        updateAccount: async (id: string, payload: {is_borrower?: boolean; is_lender?: boolean; manager_user_id?: string; company_name?: string}) => {
          const response = await axios.patch(`${API_URL}/accounts/${id}`, payload)
          return response.data
        },
        createBankAccount: async (payload: BankAccount) => {
          const {data} = await axios.post(`${API_URL}/bank-accounts`, payload)
          return data
        },
        updateBankAccount: async (id: string, payload: BankAccount) => {
          const {data} = await axios.patch(`${API_URL}/bank-accounts/${id}`, payload)
          return data
        },
        assignBankAccountToLoan: async (loanId: string, bankAccountId: string, side: 'borrower' | 'lender') => {
          const {data} = await axios.post(`${API_URL}/loans/${loanId}/bank-accounts`, {bank_account_id: bankAccountId, side})
          return data
        },
        removeBankAccountFromLoan: async (loanId: string, side: 'borrower' | 'lender') => {
          const {data} = await axios.delete(`${API_URL}/loans/${loanId}/bank-accounts/${side}`)
          return data
        },
        removeBankAccount: async (id: string) => {
          const {data} = await axios.delete(`${API_URL}/bank-accounts/${id}`)
          return data
        },
        getBankAccount: async (id: string) => {
          const {data} = await axios.get(`${API_URL}/bank-accounts/${id}`)
          return data
        },
        getBankAccountsByLoanId: async (loanId: string) => {
          const {data} = await axios.get(`${API_URL}/bank-accounts/loan/${loanId}`)
          return data
        },
        getSupportedCurrencies: async () => {
          const {data} = await axios.get(`${API_URL}/collaterals/supported-currencies`)
          return data
        },
        updateOrganizationStatus: async (id: string, role: 'borrower' | 'lender', enabled: boolean) => {
          const {data} = await axios.post(`${API_URL}/accounts/${id}/status`, {role, enabled})
          return data
        },
        updateOrganizationManagerStatus: async (id: string, enabled: boolean) => {
          const {data} = await axios.post(`${API_URL}/accounts/${id}/manager-status`, {enabled})
          return data
        },
        getOrganizationStatus: async (id: string) => {
          const {data} = await axios.get(`${API_URL}/accounts/${id}`)
          return data
        },
        removeUserFromOrganization: async (organizationId: string, userId: string) => {
          const {data} = await axios.delete(`${API_URL}/accounts/${organizationId}/users/${userId}`)
          return data
        },
        addUserToOrganization: async (organizationId: string, payload: {email: string; first_name: string; last_name: string}) => {
          const {data} = await axios.post(`${API_URL}/accounts/${organizationId}/users`, payload)
          return data
        },
        updateCustodyAddress: async (payload: {loanId: string; address: string; reason: string}) => {
          const {loanId, ...data} = payload
          const {data: responseData} = await axios.patch(`${API_URL}/loans/${loanId}/collateral-address`, data)
          return responseData
        },
        updateOrganizationTimezone: async (id: string, payload: {timezone: string}) => {
          const {data} = await axios.patch(`${API_URL}/accounts/${id}/timezone`, payload)
          return data
        },
        validateLoanActivation: async (id: string) => {
          const {data} = await axios.get(`${API_URL}/loans/${id}/validate`)
          return data
        },
        getLoanPreferences: async (loan_id: string) => {
          const {data} = await axios.get(`${API_URL}/loan-preferences?loan_id=${loan_id}`)
          return data
        },
        updateLoanPreferences: async (preferences_id: string, payload: any) => {
          const {data} = await axios.patch(`${API_URL}/loan-preferences/${preferences_id}`, payload)
          return data
        },
        createLoanPreferences: async (payload: any) => {
          const {data} = await axios.post(`${API_URL}/loan-preferences`, payload)
          return data
        }
      })}>
      {children}
    </ResourceApiServiceProvider>
  )
}

const useLendingService = () => {
  const context = useContext(LendingServiceContext)
  if (context === undefined) {
    throw new Error('LendingServiceContext was not provided. Make sure your component is a child of the LendingServiceProvider.')
  }
  return context
}

type ExtendedOrganizationAccount = OrganizationAccount & {
  users?: User[]
}

const useLendingAccountQuery = (id: string, props?: any) => {
  const lendingService = useLendingService()
  return useSimpleQuery({
    queryKey: `lending-account-get-${id}`,
    queryFn: async () => lendingService.getAccount(id),
    ...(props || {})
  }) as QueryObserverSuccessResult<ExtendedOrganizationAccount>
}

const useLendingAccountsQuery = (props?: any) => {
  const lendingService = useLendingService()
  return useSimpleQuery({
    queryKey: `lending-accounts-list`,
    queryFn: async () => lendingService.listAccounts(),
    ...(props || {})
  }) as {data: OrganizationAccount[]}
}

const useLendingLoanQuery = (id: string, props?: any) => {
  const lendingService = useLendingService()
  return useSimpleQuery({
    queryKey: `lending-loan-get-${id}`,
    queryFn: async () => lendingService.getLoan(id),
    ...(props || {})
  }) as {data: LoanWithIncludes}
}

const useLendingLoanEventsQuery = (id: string, query?: {page?: number; pageSize?: number}, props?: any) => {
  const lendingService = useLendingService()

  return useSimpleQuery({
    queryKey: ['lending-loan-events-list', id, query],
    enabled: !!id,
    queryFn: async () => lendingService.listEvents(id, query),
    ...(props || {})
  }) as {data: {data: Event[]; meta: {total: number; page: number; pageSize: number; totalPages: number; hasNextPage: boolean; hasPreviousPage: boolean}}}
}

const useLiquidationEventsQuery = (loanId: string, props?: any) => {
  const lendingService = useLendingService()

  return useSimpleQuery({
    queryKey: ['loan-liquidation-events', loanId],
    enabled: !!loanId,
    queryFn: async () => lendingService.getLiquidationEvents(loanId),
    ...(props || {})
  })
}

const useLendingLoansQuery = (props: {query?: any; [key: string]: any} = {}) => {
  const lendingService = useLendingService()
  const {query, ...otherProps} = props
  return useSimpleQuery({
    queryKey: ['lending-loans-list', query],
    queryFn: async () => lendingService.listLoans(query),
    ...(otherProps || {})
  })
}

const useLendingDepositInfoQuery = (id: string, props?: any) => {
  const lendingService = useLendingService()
  return useSimpleQuery({
    queryKey: `lending-loan-deposit-info-${id}`,
    queryFn: async () => lendingService.getDepositInfo(id),
    ...(props || {})
  }) as {data: LoanDepositInfo}
}

const useLendingPaymentsQuery = (props?: any) => {
  const lendingService = useLendingService()
  return useSimpleQuery({
    queryKey: `lending-payments-list`,
    queryFn: async () => lendingService.listPayments(),
    ...(props || {})
  })
}

const useSupportedCurrenciesQuery = (props?: any) => {
  const lendingService = useLendingService()
  return useSimpleQuery({
    queryKey: 'lending-supported-currencies',
    queryFn: async () => lendingService.getSupportedCurrencies(),
    ...(props || {})
  })
}

const useLendingServiceQuery = ({path, ...props}: {path: string; [key: string]: any}) => {
  const lendingService = useLendingService()
  return useSimpleQuery({
    queryKey: `lending-service-${path}`,
    queryFn: async () => {
      if (path === '/accounts/me') {
        return lendingService.getAccount('me')
      }
      throw new Error(`Path ${path} not supported in useLendingServiceQuery`)
    },
    ...props
  })
}

export {
  useLendingService,
  LendingServiceProvider,
  useLendingAccountsQuery,
  useLendingAccountQuery,
  useLendingPaymentsQuery,
  useLendingLoanQuery,
  useLendingLoansQuery,
  useLendingLoanEventsQuery,
  useLendingDepositInfoQuery,
  useSupportedCurrenciesQuery,
  useLendingServiceQuery,
  useLiquidationEventsQuery
}
